import { useMemo, useState, useCallback } from "react";
import type { PdfTextPage } from "./usePdfTextIndex";

export type PdfSearchMatchRect = {
  pageNumber: number;
  startIndex: number;
  text: string;
  rect: { x: number; y: number; w: number; h: number };
};

export function usePdfSearchWithRects(pages: PdfTextPage[]) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const matches = useMemo(() => {
    if (!query.trim()) return [];

    const q = query.toLowerCase();
    const result: PdfSearchMatchRect[] = [];

    for (const page of pages) {
      for (const item of page.items) {
        const lowerText = item.text.toLowerCase();
        let idx = lowerText.indexOf(q);
        while (idx !== -1) {
          result.push({
            pageNumber: page.pageNumber,
            startIndex: idx,
            text: item.text.substring(idx, idx + query.length),
            rect: item.rect,
          });
          idx = lowerText.indexOf(q, idx + 1);
        }
      }
    }

    return result;
  }, [pages, query]);

  const nextMatch = useCallback(() => {
    if (matches.length === 0) return;
    setActiveIndex((prev) => (prev + 1) % matches.length);
  }, [matches.length]);

  const prevMatch = useCallback(() => {
    if (matches.length === 0) return;
    setActiveIndex((prev) => (prev - 1 + matches.length) % matches.length);
  }, [matches.length]);

  const clearSearch = useCallback(() => {
    setQuery("");
    setActiveIndex(0);
  }, []);

  const toggleSearch = useCallback(() => {
    setIsSearchOpen((prev) => {
      if (prev) {
        setQuery("");
        setActiveIndex(0);
      }
      return !prev;
    });
  }, []);

  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const currentMatch = matches[activeIndex] ?? null;

  return {
    query,
    setQuery,
    matches,
    activeIndex,
    setActiveIndex,
    isSearchOpen,
    toggleSearch,
    openSearch,
    nextMatch,
    prevMatch,
    clearSearch,
    hasResults: matches.length > 0,
    currentMatch,
    resultCount: matches.length,
  };
}
